import axios from "axios";
import { AUTH_MODE } from "../config/cognitoConfig";
import { getIdToken } from "./cognitoAuthService";
import { getSelectedClientId, isAllSelected, withClient } from "./clientSelection";
import { mergeDashboards, mergeFrameworks, mergeLists } from "./rollup";
import { mockApi } from "../mock/mockApi";

// One place that knows how to talk to the backend. Pages call these functions
// and never care whether the data comes from the Spring Boot API or the
// built-in sample data (REACT_APP_USE_MOCK=true).

const USE_MOCK = process.env.REACT_APP_USE_MOCK === "true";

const http = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL || ""}/v1`,
});

http.interceptors.request.use(async (config) => {
  if (AUTH_MODE === "cognito") {
    const token = await getIdToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  // The "All departments" view asks for each department separately (see rollup below), so it never
  // sends the special ALL value; a request that already names a department keeps it.
  const clientId = getSelectedClientId();
  if (clientId && !isAllSelected() && !config.headers["X-Client-Id"]) config.headers["X-Client-Id"] = clientId;
  return config;
});

const get = (path, params, clientId) =>
  http.get(path, { params, headers: clientId ? { "X-Client-Id": String(clientId) } : {} }).then((r) => r.data);

// ── "All departments" (staff, read-only) ─────────────────────────────────────
// The API answers for one department per request, so for the combined view we ask for every
// department and merge the answers. Single-record calls (an assessment, a save) never use this.
let departments = [];

// run(clientId) must start the request for that department right away.
function forEachDepartment(run) {
  return Promise.all(departments.map((dept) => run(dept.id).then((data) => ({ dept, data }))));
}

// Real API: one call per department. Sample-data mode: the mock is asked as if that department were selected.
const read = (mockFn, path, params) => (id) => (USE_MOCK ? withClient(id, mockFn) : get(path, params, id));

function scoped(mockFn, path, merge = mergeLists, params) {
  const one = () => (USE_MOCK ? mockFn() : get(path, params));
  return () => (isAllSelected() ? forEachDepartment(read(mockFn, path, params)).then(merge) : one());
}

export const api = {
  getMe: () =>
    (USE_MOCK ? mockApi.getMe() : get("/me")).then((me) => {
      if (me && Array.isArray(me.availableClients)) departments = me.availableClients;
      return me;
    }),
  getDashboard: scoped(() => mockApi.getDashboard(), "/dashboard/summary", mergeDashboards),

  getApplications: scoped(() => mockApi.getApplications(), "/ai-applications"),
  createApplication: (request) =>
    USE_MOCK ? mockApi.createApplication(request) : http.post("/ai-applications", request).then((r) => r.data),
  updateApplicationStatus: (id, body) =>
    USE_MOCK
      ? mockApi.updateApplicationStatus(id, body)
      : http.patch(`/ai-applications/${id}/status`, body).then((r) => r.data),

  getAssessment: (appId) =>
    USE_MOCK ? mockApi.getAssessment(appId) : get(`/ai-applications/${appId}/assessment`),
  saveAssessment: (appId, body) =>
    USE_MOCK ? mockApi.saveAssessment(appId, body) : http.put(`/ai-applications/${appId}/assessment`, body).then((r) => r.data),
  submitAssessment: (appId) =>
    USE_MOCK ? mockApi.submitAssessment(appId) : http.post(`/ai-applications/${appId}/assessment/submit`).then((r) => r.data),
  reviewAssessment: (appId, body) =>
    USE_MOCK ? mockApi.reviewAssessment(appId, body) : http.post(`/ai-applications/${appId}/assessment/review`, body).then((r) => r.data),

  getFrameworks: scoped(() => mockApi.getFrameworks(), "/frameworks", mergeFrameworks),
  getControls: (frameworkId) => {
    if (isAllSelected()) {
      // In the combined view a framework is identified by its code (ids differ per department).
      return forEachDepartment(read(() => mockApi.getControls(), "/controls")).then((parts) =>
        mergeLists(parts).filter((c) => !frameworkId || c.frameworkCode === frameworkId)
      );
    }
    return USE_MOCK ? mockApi.getControls(frameworkId) : get("/controls", frameworkId ? { frameworkId } : undefined);
  },
  getEvidence: scoped(() => mockApi.getEvidence(), "/evidence"),
  getRisks: scoped(() => mockApi.getRisks(), "/risks"),
  getPolicies: scoped(() => mockApi.getPolicies(), "/policies"),
  getAudits: scoped(() => mockApi.getAudits(), "/audits"),
  getFindings: scoped(() => mockApi.getFindings(), "/findings"),
  getVendors: scoped(() => mockApi.getVendors(), "/vendors"),

  // Vendor contracts and the AI that reads them (one department at a time).
  getContracts: (vendorId) => (USE_MOCK ? mockApi.getContracts(vendorId) : get(`/vendors/${vendorId}/contracts`)),
  uploadContract: (vendorId, file) => {
    if (USE_MOCK) return mockApi.uploadContract(vendorId, file);
    const form = new FormData();
    form.append("file", file);
    return http.post(`/vendors/${vendorId}/contracts`, form).then((r) => r.data);
  },
  analyzeContract: (id) => (USE_MOCK ? mockApi.analyzeContract(id) : http.post(`/contracts/${id}/analyze`).then((r) => r.data)),
  deleteContract: (id) => (USE_MOCK ? mockApi.deleteContract(id) : http.delete(`/contracts/${id}`).then((r) => r.data)),
  getContractUrl: (id) => (USE_MOCK ? mockApi.getContractUrl(id) : get(`/contracts/${id}/download`)),
  // scope: {} for every contract, { vendorId } for one vendor, { contractId } for one document.
  askContracts: (question, scope = {}) =>
    USE_MOCK
      ? mockApi.askContracts({ question, ...scope })
      : http.post("/contracts/ask", { question, vendorId: scope.vendorId || null, contractId: scope.contractId || null }).then((r) => r.data),

  // Users & approvals (platform admins). These are not department data, so no department header is needed.
  getUsers: () => (USE_MOCK ? mockApi.getUsers() : get("/admin/users")),
  setUserAccess: (username, body) =>
    USE_MOCK
      ? mockApi.setUserAccess(username, body)
      : http.put(`/admin/users/${encodeURIComponent(username)}/access`, body).then((r) => r.data),
  removeUser: (username) =>
    USE_MOCK ? mockApi.removeUser(username) : http.delete(`/admin/users/${encodeURIComponent(username)}`).then((r) => r.data),
};

export function errorMessage(error) {
  return (
    error?.response?.data?.message ||
    (error?.response?.status === 403 ? "You don't have permission to do that." : null) ||
    error?.message ||
    "Something went wrong."
  );
}
